import React from 'react';

import { Control, Controller } from 'react-hook-form';

import { Autocomplete, Chip, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';

import { TourOfferFormOptions } from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourSchedulesEditor from './NativeTourSchedulesEditor';
import { NEW_DEPARTURES_YEARS, NativeTourForm, newDeparturesProblem } from './nativeTourForm';

const NEW_DATES = 'new';

interface Props {
  control: Control<NativeTourForm>;
  options: TourOfferFormOptions | undefined;
}

type PriceDisplayField = 'content.showPricePerDay' | 'content.showValuedAtPrice' | 'content.highlightPackages';

const PRICE_DISPLAY: ReadonlyArray<{ name: PriceDisplayField; label: string; description: string }> = [
  { name: 'content.showPricePerDay', label: 'Price per day', description: 'Show the price divided by the tour length' },
  {
    name: 'content.showValuedAtPrice',
    label: 'Valued at price',
    description: 'Show the was price beside the price',
  },
  { name: 'content.highlightPackages', label: 'Highlight packages', description: '' },
];

/** How the tour is marketed while it's on sale. */
export default function NativeTourOnSaleSection({ control, options }: Props) {
  const campaignTags = options?.campaignTags ?? [];
  return (
    <Stack spacing={3}>
      <NativeTourCard
        title="Campaign schedules"
        description="When and where the tour is on sale. Times are Sydney time."
      >
        <Controller
          control={control}
          name="schedules"
          render={({ field }) => (
            <NativeTourSchedulesEditor value={field.value} onChange={field.onChange} options={options} />
          )}
        />
      </NativeTourCard>

      <NativeTourCard
        title="Campaign tags and email"
        description="The campaigns the tour is part of, and its email subject line."
      >
        <Stack spacing={2}>
          <Controller
            control={control}
            name="content.campaignTags"
            render={({ field }) => {
              const unknown = field.value.filter((tag) => campaignTags.length > 0 && !campaignTags.includes(tag));
              return (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={campaignTags}
                  value={field.value}
                  onChange={(_event, next) => field.onChange(next)}
                  renderTags={(values, getTagProps) =>
                    values.map((value, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={value}
                        size="small"
                        label={value}
                        color={unknown.includes(value) ? 'warning' : 'default'}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Campaign tags"
                      helperText={
                        unknown.length > 0
                          ? `No longer a campaign: ${unknown.join(', ')}`
                          : 'The campaigns in svc-offer the tour is part of'
                      }
                    />
                  )}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="content.emailSubjectLine"
            render={({ field }) => (
              <TextField
                label="Email subject line"
                helperText="The short name used in marketing emails and search"
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value.trim() === '' ? null : event.target.value)}
                fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name="emailPreHeader"
            render={({ field }) => (
              <TextField
                label="Email preheader"
                helperText="The preview text after the subject line in marketing emails"
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value.trim() === '' ? null : event.target.value)}
                fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name="newDepartures"
            render={({ field }) => {
              const problem = newDeparturesProblem(field.value);
              return (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value.added}
                        onChange={(event) =>
                          field.onChange({
                            added: event.target.checked,
                            year: event.target.checked ? field.value.year : null,
                          })
                        }
                      />
                    }
                    label={
                      <Stack>
                        <Typography>New departures added</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tags the tour "{field.value.year ?? 'New'} Dates Added"
                        </Typography>
                      </Stack>
                    }
                  />
                  {field.value.added && (
                    <TextField
                      select
                      label="Year"
                      value={field.value.year ?? NEW_DATES}
                      onChange={(event) =>
                        field.onChange({
                          added: true,
                          year: event.target.value === NEW_DATES ? null : Number(event.target.value),
                        })
                      }
                      error={!!problem}
                      helperText={problem}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value={NEW_DATES}>No year ("New")</MenuItem>
                      {[...new Set([...(field.value.year ? [field.value.year] : []), ...NEW_DEPARTURES_YEARS])].map(
                        (year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  )}
                </Stack>
              );
            }}
          />
        </Stack>
      </NativeTourCard>

      <NativeTourCard title="Price display" description="How prices are shown on the tour page.">
        <Stack spacing={1}>
          {PRICE_DISPLAY.map(({ name, label, description }) => (
            <Controller
              key={name}
              control={control}
              name={name}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                  label={
                    <Stack>
                      <Typography>{label}</Typography>
                      {description && (
                        <Typography variant="body2" color="text.secondary">
                          {description}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
              )}
            />
          ))}
        </Stack>
      </NativeTourCard>
    </Stack>
  );
}
